import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Gift, Clock } from 'lucide-react';

interface NewsletterPopupProps {
  onSubscribe: (email: string) => void;
}

const DISMISSED_KEY = 'sggadgets_newsletter_dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;
const TRIGGER_DELAY = 15000;

export function NewsletterPopup({ onSubscribe }: NewsletterPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < DISMISS_DURATION) return;
    }

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, TRIGGER_DELAY);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isOpen && !submitted) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            handleClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isOpen, submitted]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      onSubscribe(email);
      setSubmitted(true);
      if (countdownRef.current) clearInterval(countdownRef.current);
      timerRef.current = setTimeout(() => {
        handleClose();
      }, 3000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md z-50"
            role="dialog"
            aria-modal="true"
            aria-label="Newsletter signup"
          >
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-primary-600 via-red-500 to-primary-700" />

              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors z-10"
                aria-label="Close popup"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="p-6 md:p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-primary-50 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                  <Gift className="w-8 h-8 text-primary-600" />
                </div>

                <h3 className="text-2xl font-bold mb-2">Get 10% Off!</h3>
                <p className="text-muted-foreground mb-1">
                  Subscribe to our newsletter and use code
                </p>
                <p className="text-lg font-bold text-primary-600 mb-6">WELCOME10</p>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="py-4"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="font-medium">Welcome aboard!</p>
                    <p className="text-sm text-muted-foreground">
                      Check your inbox for your discount code.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full pl-11 pr-4 py-3 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                        required
                        aria-label="Email for newsletter"
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
                    >
                      Claim My 10% Off
                    </button>
                  </form>
                )}

                <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Auto-closes in {countdown}s</span>
                </div>

                <p className="mt-3 text-xs text-muted-foreground">
                  No spam, unsubscribe anytime.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
